/**
 * Blueprint proof-state ladder.
 *
 * Encodes how strongly a Blueprint runtime object is proven. Higher ranks
 * imply every weaker guarantee. Product-ready Blueprint claims require
 * `operator-runtime-proof` or stronger; synthetic runtime proof is a demo bar.
 */
export type BlueprintProofStateDto =
  | 'fixture-test-proof'
  | 'source-path-proof'
  | 'product-runtime-synthetic-proof'
  | 'operator-runtime-proof'
  | 'market-proof';

export const BLUEPRINT_PROOF_STATE_LADDER: readonly BlueprintProofStateDto[] = [
  'fixture-test-proof',
  'source-path-proof',
  'product-runtime-synthetic-proof',
  'operator-runtime-proof',
  'market-proof',
] as const;

export type BlueprintProofStateRank = 0 | 1 | 2 | 3 | 4;

export type BlueprintProofStateTransitionDto = {
  from: BlueprintProofStateDto;
  to: BlueprintProofStateDto;
};

export function isBlueprintProofState(value: unknown): value is BlueprintProofStateDto {
  return typeof value === 'string' && BLUEPRINT_PROOF_STATE_LADDER.includes(value as BlueprintProofStateDto);
}

export function blueprintProofStateRank(state: BlueprintProofStateDto): BlueprintProofStateRank {
  const rank = BLUEPRINT_PROOF_STATE_LADDER.indexOf(state);
  if (rank < 0) {
    throw new Error(`Unknown blueprint proof state: ${String(state)}`);
  }
  return rank as BlueprintProofStateRank;
}

export function isBlueprintProductReadyProof(state: BlueprintProofStateDto): boolean {
  return blueprintProofStateRank(state) >= blueprintProofStateRank('operator-runtime-proof');
}

export function isBlueprintNonProductProof(state: BlueprintProofStateDto): boolean {
  return blueprintProofStateRank(state) <= blueprintProofStateRank('source-path-proof');
}

export function meetsBlueprintProofState(
  actual: BlueprintProofStateDto,
  required: BlueprintProofStateDto,
): boolean {
  return blueprintProofStateRank(actual) >= blueprintProofStateRank(required);
}

export function assertBlueprintProofStateTransition(
  from: BlueprintProofStateDto,
  to: BlueprintProofStateDto,
): BlueprintProofStateTransitionDto {
  if (blueprintProofStateRank(to) < blueprintProofStateRank(from)) {
    throw new Error(`Blueprint proof state cannot transition from '${from}' to weaker state '${to}'.`);
  }

  return { from, to };
}

export function transitionBlueprintProofState(
  from: BlueprintProofStateDto,
  to: BlueprintProofStateDto,
): BlueprintProofStateDto {
  assertBlueprintProofStateTransition(from, to);
  return to;
}
